import { create } from 'zustand';
import api from '@/lib/api';

interface ProgressState {
  // Data
  stars: number;
  animalsViewed: number;
  achievementsEarned: number;

  // For animation
  pendingStars: number;

  // Loading state
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;

  // Actions
  fetchProgress: () => Promise<void>;
  addStars: (count: number) => void;
  reset: () => void;
}

export const useProgressStore = create<ProgressState>((set, get) => ({
  // Initial state
  stars: 0,
  animalsViewed: 0,
  achievementsEarned: 0,
  pendingStars: 0,
  isLoading: false,
  isLoaded: false,
  error: null,

  // Fetch progress from API
  fetchProgress: async () => {
    // Don't refetch if already loaded
    if (get().isLoaded) return;

    set({ isLoading: true, error: null });

    try {
      const response = await api.get('/api/progress');
      const { stars, animals_viewed, achievements_earned } = response.data;

      set({
        stars: stars || 0,
        animalsViewed: animals_viewed || 0,
        achievementsEarned: achievements_earned || 0,
        isLoading: false,
        isLoaded: true,
      });
    } catch (error) {
      // Not authenticated or error - just set defaults
      set({
        stars: 0,
        animalsViewed: 0,
        achievementsEarned: 0,
        isLoading: false,
        isLoaded: true,
        error: null, // Don't show error for auth failures
      });
    }
  },

  // Add stars (with animation trigger)
  addStars: (count) => {
    if (count <= 0) return;

    set(state => ({
      pendingStars: count,
      stars: state.stars + count,
    }));

    // Clear pending after animation
    setTimeout(() => {
      set({ pendingStars: 0 });
    }, 1500);
  },

  // Reset state (e.g., on logout)
  reset: () => set({
    stars: 0,
    animalsViewed: 0,
    achievementsEarned: 0,
    pendingStars: 0,
    isLoading: false,
    isLoaded: false,
    error: null,
  }),
}));
